import { z } from 'zod';
import { agentIdSchema, publicAgentSchema } from '../agent/agent.schemas.js';
import { operationIdSchema } from '../operation/operation.schemas.js';
import { isoUtcDateTimeSchema, positiveIntIdSchema } from '../shared/schemas.js';

export const missionIdSchema = positiveIntIdSchema;

// READY : agent inscrit, operation pas encore demarree (lobby).
// SUCCEEDED : gagnant ; FAILED : agent elimine ; ABORTED : abandon ou depart du lobby.
export const missionStatusCodeSchema = z.enum(['READY', 'STARTED', 'ABORTED', 'SUCCEEDED', 'FAILED']);
export type MissionStatusCode = z.infer<typeof missionStatusCodeSchema>;

export const missionSchema = z.object({
  id: z.number().int().positive(),
  operationId: operationIdSchema,
  agentId: agentIdSchema,
  statusCode: missionStatusCodeSchema,
  startedAt: isoUtcDateTimeSchema.nullable(),
  endedAt: isoUtcDateTimeSchema.nullable(),
  abandonedAt: isoUtcDateTimeSchema.nullable(),
  succeededTargetCount: z.number().int().nonnegative(),
  archivedAt: isoUtcDateTimeSchema.nullable(),
  createdAt: isoUtcDateTimeSchema,
  updatedAt: isoUtcDateTimeSchema,
});

export type Mission = z.infer<typeof missionSchema>;

// Mission telle que son agent la lit : operationPublicId mene a la page
// publique de l'operation (compte a rebours, nombre d'agents, TD-021).
export const playerMissionSchema = missionSchema.extend({
  operationPublicId: z.string(),
});

export type PlayerMission = z.infer<typeof playerMissionSchema>;

// Cible en cours d'une mission, telle que le joueur la voit : qui eliminer et
// avec quelle action. id sert a declarer l'elimination.
export const missionTargetSchema = z.object({
  id: positiveIntIdSchema,
  agent: z.object({
    firstName: z.string(),
    codeName: z.string(),
  }),
  action: z.object({
    id: positiveIntIdSchema,
    description: z.string(),
  }),
  assignedAt: isoUtcDateTimeSchema,
  // Elimination declaree, en attente de confirmation par la victime.
  claimedAt: isoUtcDateTimeSchema.nullable(),
  // Instant a partir duquel le joueur peut de nouveau changer d'action, quelle
  // que soit sa cible ; null s'il n'en a jamais change (TD-031).
  nextActionChangeAt: isoUtcDateTimeSchema.nullable(),
});

export type MissionTarget = z.infer<typeof missionTargetSchema>;

// Delai minimal entre deux changements d'action demandes par un meme joueur (TD-031).
export const ACTION_CHANGE_COOLDOWN_MS = 5 * 60_000;

// Delai laisse a la victime pour confirmer ou contester une elimination declaree
// par son chasseur ; au-dela, l'elimination est validee (TD-015).
export const ELIMINATION_CONFIRMATION_DELAY_MS = 60_000;

// Elimination declaree contre l'agent : il peut la confirmer ou la contester
// jusqu'a expiresAt, au-dela elle est validee automatiquement. L'action
// realisee lui est montree, pas le chasseur : une victime qui conteste reste en
// jeu et ne doit pas decouvrir qui la chasse (TD-030).
export const pendingEliminationSchema = z.object({
  claimedAt: isoUtcDateTimeSchema,
  expiresAt: isoUtcDateTimeSchema,
  action: z.object({ description: z.string() }),
});

export type PendingElimination = z.infer<typeof pendingEliminationSchema>;

export const targetIdSchema = positiveIntIdSchema;

// Debriefing d'un agent sur sa propre mission (TD-021) : ses victimes et, s'il
// a ete elimine, l'agent qui l'a elimine avec l'action realisee. Prenoms et
// noms de code (TD-029).
export const missionDebriefSchema = publicAgentSchema.extend({
  statusCode: missionStatusCodeSchema,
  score: z.number().int().nonnegative(),
  eliminatedTargets: z.array(
    publicAgentSchema.extend({
      eliminatedAt: isoUtcDateTimeSchema,
    }),
  ),
  elimination: z
    .object({
      by: publicAgentSchema,
      action: z.object({ description: z.string() }),
      eliminatedAt: isoUtcDateTimeSchema,
    })
    .nullable(),
});

export type MissionDebrief = z.infer<typeof missionDebriefSchema>;
